import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { CompanyLogoModel } from 'src/models/v2/account';
import { useGetCompanyLogoBySubdomainQuery, useGetUIThemeByIdQuery } from 'src/queries/services/account';
import { subscribe } from '../utils/vigoCustomEvent';

interface DynamicThemeContextType {
  userTheme: any;
  companyLogo: CompanyLogoModel | null;
  isLoading: boolean;
  error: Error | null;
}

const DynamicThemeContext = createContext<DynamicThemeContextType>({
  userTheme: null,
  companyLogo: null,
  isLoading: false,
  error: null
});

export const useDynamicTheme = () => useContext(DynamicThemeContext);

export const DynamicThemeProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();

  const { data: userTheme, isLoading: userThemeIsLoading, refetch: refetchTheme } = useGetUIThemeByIdQuery(
    auth.user?.profile?.tenantId as string,
    {
      enabled: Boolean(auth.isAuthenticated) && Boolean(auth.user?.profile?.tenantId),
      initialData: null,
      onError: err => {
        setError(err instanceof Error ? err : new Error('Unknown error fetching theme'));
        console.error('Error fetching user theme:', err);
      },
      retry: false
    }
  );

  const {
    data: companyLogo,
    isLoading: companyLogoIsLoading,
    refetch: refetchLogo
  } = useGetCompanyLogoBySubdomainQuery(auth.user?.profile?.subdomain as string, {
    enabled: Boolean(auth.isAuthenticated) && Boolean(auth.user?.profile?.subdomain),
    onError: err => {
      setError(err instanceof Error ? err : new Error('Unknown error fetching company logo'));
      console.error('Error fetching company logo:', err);
    },
    retry: false
  });

  const isLoading = userThemeIsLoading || companyLogoIsLoading;

  useEffect(() => {
    const handleAccountSaveEvent = () => {
      refetchTheme();
      refetchLogo();
    };

    const unsubscribe = subscribe('account_save_event', handleAccountSaveEvent);

    return () => {
      unsubscribe();
    };
  }, [refetchTheme, refetchLogo]);

  return (
    <DynamicThemeContext.Provider value={{ userTheme, companyLogo: companyLogo ?? null, isLoading, error }}>
      {children}
    </DynamicThemeContext.Provider>
  );
};
